/**
 * The frame around the till, which is almost no frame at all.
 *
 * WHY THERE IS NO NAVIGATION HERE
 * -------------------------------
 * A cashier on shift is on one screen for hours. Navigation is a distraction
 * and, worse, a row of targets next to the Charge button — which is exactly
 * where a mis-tap costs the most.
 *
 * It is also space. On a 1024px tablet a sidebar and the cart together leave
 * about 448px for the products: two cramped columns. Without it there is 704px
 * and four comfortable ones, which is the difference between hunting for a
 * dish and tapping it.
 *
 * WHAT IS HERE INSTEAD
 * --------------------
 * The three things a cashier needs to see without looking for them: which
 * shift they are on, whether their trading has reached head office, and the
 * way out. Ending a shift lives here rather than beside the cart because it is
 * a decision about the day, not about the order in front of them.
 */
import { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import { useAuth } from '../../stores/auth.js';
import { useShift } from '../../features/pos/useShift.js';
import { useBusinessDay } from '../../features/pos/useBusinessDay.js';
import { SyncIndicator } from './SyncIndicator.jsx';
import { elsewhereFor } from './nav.js';
import logo from '../../assets/logo.png';
import { OnlineOrders } from '../../features/online-orders/OnlineOrders.jsx';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A YYYY-MM-DD as a person reads it: "12 Sep". Built by hand so it reads the
 *  same on every device, not however the browser's locale renders it. */
function shortDay(iso) {
  const [y, m, d] = String(iso ?? '').split('-').map(Number);
  if (!y || !m || !d) return iso || '';
  return `${String(d).padStart(2, '0')} ${MONTHS[m - 1]}`;
}

export function TillShell() {
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const can = useAuth((s) => s.can);

  // After `can`, not before it — a const read above its own declaration
  // throws, and a blank till screen is a hard fault to read backwards.
  const elsewhere = elsewhereFor(can);

  // The same query the till uses; asking twice costs nothing because the
  // answer is cached, and it keeps the shift number in the bar where a
  // cashier can see it without leaving what they are doing.
  const { shift: active, isOpen, mine, canClose, cashierName } = useShift();
  const shift = active?.shift;

  // The trading day the shop currently has open — shown in the bar so a cashier
  // always knows which day their sales are landing on, and closed from here.
  const { current: businessDay } = useBusinessDay();
  const openDay = businessDay.data?.open ?? null;

  const [closing, setClosing] = useState(false);
  const [closingDay, setClosingDay] = useState(false);

  return (
    <div className="flex h-dvh flex-col">
      <header className="flex h-12 shrink-0 items-center gap-3 border-b border-rule bg-surface px-3">
        <img src={logo} alt="Food Run" className="h-6 w-auto shrink-0" width="512" height="97" />

        {shift?.shiftNumber && (
          <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-brand-400/30 bg-brand-400/15 px-2.5 py-1 text-xs font-medium text-brand-300">
            <span className="size-1.5 rounded-full bg-brand-400" aria-hidden="true" />
            {shift.shiftNumber}
            {/* Somebody else's shift: say whose, since every sale here lands on it. */}
            {!mine && cashierName && <span className="text-brand-300/80">· {cashierName}</span>}
          </span>
        )}

        {openDay && (
          <span
            className="hidden shrink-0 items-center gap-1.5 rounded-full border border-rule bg-surface-3 px-2.5 py-1 text-xs text-ink-2 sm:inline-flex"
            title="The trading day now open"
          >
            Day: <span className="font-medium text-ink">{shortDay(openDay.businessDate)}</span>
          </span>
        )}

        <div className="flex-1" />

        <SyncIndicator />

        {/*
          The way off this screen, for anyone who has somewhere else to be.
          Deliberately quiet — for most of a shift this is the only screen
          anybody wants — but present, which it was not.

          It used to be offered only to people who could read reports, and a
          cashier cannot. So a cashier landed here with the floor plan, the
          kitchen screen and the order list all permitted and none of them
          reachable without signing out. It now goes to the first screen they
          can actually get to, which carries the navigation for the rest.
        */}
        {elsewhere && (
          <button
            onClick={() => navigate(elsewhere.to)}
            className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-ink-3 hover:bg-surface-3 hover:text-ink-2"
          >
            {elsewhere.to === '/reports' ? 'Back office' : elsewhere.label}
          </button>
        )}

        <span className="hidden text-sm text-ink-2 sm:inline">
          {user?.firstName ?? user?.email}
        </span>

        {/* Closing the day is the end-of-night action — separate from ending a
            shift, and quieter, because it is not a cash count. Hidden while
            either close screen is up so the bar does not offer a way back into
            the thing being done. */}
        {openDay && !closing && !closingDay && (
          <button
            onClick={() => setClosingDay(true)}
            className="rounded-lg border border-rule px-3 py-1.5 text-xs font-medium text-ink-2 hover:bg-surface-3 hover:text-ink"
          >
            Close the day
          </button>
        )}

        {isOpen && canClose && !closing && !closingDay && (
          <button
            onClick={() => setClosing(true)}
            className="rounded-lg border border-bad/30 bg-bad-bg px-3 py-1.5 text-xs font-medium text-bad hover:brightness-125"
          >
            End shift
          </button>
        )}
      </header>

      <main className="min-h-0 flex-1">
        {/* The till decides what to draw; the shell only says whether a shift
            or the day is being closed. */}
        <Outlet context={{ closing, setClosing, closingDay, setClosingDay }} />
          {/* Website orders waiting for an answer, wherever the cashier happens to be. */}
          <OnlineOrders />
      </main>
    </div>
  );
}
